import React, { useState, useEffect } from "react";
import "../styles/grievance.css";
import "../styles/pageConfiguration.css";
import "../styles/sessionModal.css";
import "../styles/toast.css";
import { CLEAR_SESSION } from "../store/constants/Constants";
import { useDispatch, useSelector } from "react-redux";
import { Slide, ToastContainer, toast } from "react-toastify";
import CustomToast from "./CustomToastContainer";
import { IcCalendarWeek } from "../custom-components/Icon";
import {
  Text,
  InputFieldV2,
  InputCheckbox,
  Tabs,
  TabItem,
  TokenProvider,
  Button,
} from "../custom-components";
import { saveGrievanceConfig, getGrievanceDetails, updateGrievanceConfig } from "../store/actions/CommonAction";
import Select from "react-select";
import ConfigurationShimmer from "./ConfigurationShimmer";

const Grievance = () => {
  const dispatch = useDispatch();
  const [endpointUrl, setEndpointUrl] = useState("");
  const [isEndpointUrlValid, setIsEndpointUrlValid] = useState(true);
  const [slaTimeline, setSlaTimeline] = useState({ value: "", unit: "DAYS" });
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [escalationPolicy, setEscalationPolicy] = useState({
    value: "",
    unit: "DAYS",
  });
  const [retentionPolicy, setRetentionPolicy] = useState({
    value: "",
    unit: "",
  });
  const [isEmailChecked, setIsEmailChecked] = useState(true);
  const [isSmsChecked, setIsSmsChecked] = useState(true);
  const [grievanceTypes, setGrievanceTypes] = useState(["ACCESS", "CORRECTION", "DELETION"]);
  const [intakeMethods, setIntakeMethods] = useState(["WEB_FORM"]);
  const [retentionDuration, setRetentionDuration] = useState("YEARS");
  const workflowOptions = [
    { value: "New", label: "New" },
    { value: "In Progress", label: "In Progress" },
    { value: "Resolved", label: "Resolved" },
  ];
  const [workflow, setWorkflow] = useState(workflowOptions.map(opt => opt.value));

  const [slaInput, setSlaInput] = useState("7");
  const [escalationInput, setEscalationInput] = useState("2");
  const [retentionInput, setRetentionInput] = useState("99");

  const tenantId = useSelector((state) => state.common.tenant_id);
  const token = useSelector((state) => state.common.session_token);
  const businessId = useSelector((state) => state.common.business_id);
  const [configId, setConfigId] = useState("");
  const [loading, setLoading] = useState(true);

  const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/\S*)?$/;

  const options = [
    { value: "ACCESS", label: "Access" },
    { value: "CORRECTION", label: "Correction" },
    { value: "DELETION", label: "Deletion" },
    { value: "OBJECTION", label: "Objection" },
  ];

  const optionsIntake = [
    { value: "WEB_FORM", label: "Web Form" },
  ];


  const customStyles = {
    control: (base, state) => ({
      ...base,
      color: "#333",
      backgroundColor: "#fff",
      border: "1px solid #ccc",
      borderRadius: "8px",
      padding: "2px 4px",
      fontSize: "14px",
      minHeight: "40px",

      // remove blue glow
      boxShadow: "none",
      borderColor: state.isFocused ? "#ccc" : "#ccc", // keep same on focus
      "&:hover": {
        borderColor: "#999",
      },
    }),

    valueContainer: (base) => ({
      ...base,
      padding: "0 8px",
      gap: "4px",
    }),

    placeholder: (base) => ({
      ...base,
      color: "#666",
      fontSize: "14px",
    }),

    multiValue: (base) => ({
      ...base,
      borderRadius: "6px",
      backgroundColor: "#f0f0f0",
      padding: "2px 6px",
    }),

    multiValueLabel: (base) => ({
      ...base,
      color: "#333",
      fontSize: "13px",
    }),

    multiValueRemove: (base) => ({
      ...base,
      color: "#666",
      cursor: "pointer",
      ":hover": {
        backgroundColor: "#e0e0e0",
        color: "#000",
      },
    }),

    menu: (base) => ({
      ...base,
      borderRadius: "8px",
      border: "1px solid #ccc",
      marginTop: "4px",
      zIndex: 9999,
    }),

    menuList: (base) => ({
      ...base,
      padding: "4px 0",
    }),

    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      padding: "8px 12px",
      cursor: "pointer",
      backgroundColor: state.isSelected
        ? "#e6f0ff" // selected background
        : state.isFocused
          ? "#f5f5f5" // hover background
          : "#fff",
      color: "#333",
      ":active": {
        backgroundColor: "#e6f0ff",
      },
    }),

    dropdownIndicator: (base) => ({
      ...base,
      color: "#666",
      padding: "0 8px",
      "&:hover": {
        color: "#333",
      },
    }),

    clearIndicator: (base) => ({
      ...base,
      padding: "0 8px",
    }),
  };

  const handleEndpointUrlChange = (e) => {
    const value = e.target.value.trim();
    setEndpointUrl(value);

    if (value === "") {
      setIsEndpointUrlValid(true); // don’t show error when empty
      return;
    }

    if (urlRegex.test(value)) {
      setIsEndpointUrlValid(true);
    } else {
      setIsEndpointUrlValid(false);
    }
  };

  const handleRententionDurationChange = (e) => {
    setRetentionDuration(e.target.value);
  };

  const handleSlaChange = (e) => setSlaInput(e.target.value);
  const handleEscalationChange = (e) => setEscalationInput(e.target.value);

  const handleRetentionChange = (e) => setRetentionInput(e.target.value);


  const handleSubmit = async () => {

    // Get input values
    const endpointUrl = document
      .querySelector("input[name='endpointUrl']")
      .value.trim();

    if (!grievanceTypes || grievanceTypes.length === 0) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please select a Grievance Type."}
          />
        ),
        { icon: false }
      );

      return;
    }

    if (!intakeMethods || intakeMethods.length === 0) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please select an Intake Method."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (!endpointUrl) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please enter an Endpoint URL."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (!workflow || workflow.length === 0) {
      toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please select Workflow Steps."}
          />
        ),
        { icon: false }
      );
      return;
    }

    if (!slaTimeline.value || !slaTimeline.unit)
      return toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please enter SLA Timelines."}
          />
        ),
        { icon: false }
      );
    if (!escalationPolicy.value || !escalationPolicy.unit)
      return toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please enter Escalation Policy."}
          />
        ),
        { icon: false }
      );
    if (!retentionPolicy.value || !retentionPolicy.unit)
      return toast.error(
        (props) => (
          <CustomToast
            {...props}
            type="error"
            message={"Please enter Retention Policy."}
          />
        ),
        { icon: false }
      );

    const requestBody = {
      configurationJson: {
        grievanceTypes,
        endpointUrl,
        intakeMethods, // wrap in array
        workflow,
        slaTimeline,
        escalationPolicy,
        retentionPolicy,
        communicationConfig: {
          email: isEmailChecked,
          sms: isSmsChecked,
        },
      },
    };

    const headers = {
      "business-id": businessId,
      "scope-level": "tenant",
      "tenant-id": tenantId,
      "x-session-token": token,
    };

    const putHeaders = {
      "tenant-id": tenantId,
      "business-id": businessId,
      "x-session-token": token,
    };

    if (!configId) {

      try {
        const resp = await dispatch(saveGrievanceConfig(requestBody, headers));
        console.log("Response from saveGrievanceConfig:", resp);
        if (resp.status == 201) {
          toast.success(
            (props) => (
              <CustomToast
                {...props}
                type="success"
                message={"Settings saved successfully!"}
              />
            ),
            { icon: false }
          );
        }
      } catch (error) {
        console.error("Error saving configuration:", error);
        if (
          error[0]?.errorCode == "JCMP4003" ||
          error[0]?.errorCode == "JCMP4001"
        ) {
          toast.error(
            (props) => (
              <CustomToast {...props} type="error" message={"Session expired"} />
            ),
            { icon: false }
          );
          dispatch({ type: CLEAR_SESSION });
          setShowSessionModal(true);
          setTimeout(() => {
            setShowSessionModal(false);
            navigate("/adminLogin");
          }, 7000);
        } else if (error[0]?.errorCode == "JCMP3003") {
          toast.error(
            (props) => (
              <CustomToast
                {...props}
                type="error"
                message={"Greviance Set up is already exists"}
              />
            ),
            { icon: false }
          );
        }
      }

    }
    else {
      try {
        const resp = await dispatch(updateGrievanceConfig(requestBody, putHeaders, configId));
        console.log("Response from updateGrievanceConfig:", resp);
        if (resp.status == 200) {
          toast.success(
            (props) => (
              <CustomToast
                {...props}
                type="success"
                message={"Settings updated successfully!"}
              />
            ),
            { icon: false }
          );
        }
      } catch (error) {
        console.error("Error saving configuration:", error);
        if (
          error[0]?.errorCode == "JCMP4003" ||
          error[0]?.errorCode == "JCMP4001"
        ) {
          toast.error(
            (props) => (
              <CustomToast {...props} type="error" message={"Session expired"} />
            ),
            { icon: false }
          );
          dispatch({ type: CLEAR_SESSION });
          setShowSessionModal(true);
          setTimeout(() => {
            setShowSessionModal(false);
            navigate("/adminLogin");
          }, 7000);
        } else if (error[0]?.errorCode == "JCMP3003") {
          toast.error(
            (props) => (
              <CustomToast
                {...props}
                type="error"
                message={"Greviance Set up is already exists"}
              />
            ),
            { icon: false }
          );
        }
      }
    }


  };

  const format = ({ value, unit }) => {
    if (!value || !unit) return "";
    // Convert to lowercase for consistency
    return `${value} ${unit.toLowerCase()}`;
  };

  useEffect(() => {
    setSlaTimeline({
      value: slaInput ? String(slaInput) : "",
      unit: "DAYS",
    });
  }, [slaInput]);

  useEffect(() => {
    setEscalationPolicy({
      value: escalationInput ? String(escalationInput) : "",
      unit: "DAYS",
    });
  }, [escalationInput]);

  useEffect(() => {
    setRetentionPolicy({
      value: retentionInput ? String(retentionInput) : "",
      unit: retentionDuration || "YEARS",
    });
  }, [retentionInput, retentionDuration]);

  useEffect(() => {
    const fetchConsentDetails = async () => {
      try {
        setLoading(true);
        const response = await getGrievanceDetails(token, tenantId, businessId);
        console.log("gri response: ", response);

        const config = response?.searchList?.[0]?.configurationJson;
        console.log("config: ", config);
        console.log("config url: ", config?.endpointUrl);
        setConfigId(response?.searchList?.[0]?.configId);
        console.log("configId: ", response?.searchList?.[0]?.configId);
        if (!config) {
          setLoading(false);
          return;
        }

        setEndpointUrl(config.endpointUrl || "");
        setSlaTimeline({
          value: config.slaTimeline?.value || "",
          unit: config.slaTimeline?.unit || "",
        });
        //  setSlaInput(format({
        //    value: config.slaTimeline?.value || "",
        //    unit: config.slaTimeline?.unit || "",
        //  }));

        setEscalationPolicy({
          value: config.escalationPolicy?.value || "",
          unit: config.escalationPolicy?.unit || "",
        });
        // setEscalationInput(format({ 
        //   value: config.escalationPolicy?.value || "",
        //   unit: config.escalationPolicy?.unit || "",
        // }));

        //  setRetentionPolicy({
        //    value: config.retentionPolicy?.value || "",
        //    unit: config.retentionPolicy?.unit || "",
        //  });
        //  setRetentionInput(format({
        //     value: config.retentionPolicy?.value || "",
        //     unit: config.retentionPolicy?.unit || "",
        //  }));

        setSlaInput(String(config.slaTimeline?.value ?? ""));
        setEscalationInput(String(config.escalationPolicy?.value ?? ""));
        setRetentionInput(String(config.retentionPolicy?.value ?? ""));
        setRetentionDuration(config.retentionPolicy?.unit ?? "");


        setRetentionPolicy({
          value: String(config.retentionPolicy?.value ?? ""),
          unit: config.retentionPolicy?.unit ?? "",
        });



        setIsEmailChecked(config.communicationConfig?.email ?? true);
        setIsSmsChecked(config.communicationConfig?.sms ?? true);
        setGrievanceTypes(config.grievanceTypes || ["ACCESS", "CORRECTION", "DELETION"]);
        setIntakeMethods(config.intakeMethods || []);
        setWorkflow(config.workflow || []);


      } catch (error) {
        console.error("Error fetching consent details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchConsentDetails();
  }, [token, tenantId, businessId]
  );

  if (loading) {
    return <ConfigurationShimmer />;
  }

  return (
    <>
      {showSessionModal && (
        <div className="session-timeout-overlay">
          <div className="session-timeout-modal">
            <Text appearance="heading-s" color="feedback_error_50">
              Session Time Out
            </Text>
            <br></br>
            <Text appearance="body-s" color="primary-80">
              Your session has expired. Please log in again.
            </Text>
          </div>
        </div>
      )}
      <div className="configurePage" style={{ padding: "32px", minHeight: "calc(100vh - 80px)", boxSizing: "border-box", width: "100%", maxWidth: "100%", overflowX: "hidden", backgroundColor: '#F8FAFC', position: 'relative' }}>
        {/* Decorative background blob */}
        <div style={{ position: 'absolute', top: '-10%', right: '-5%', width: '40%', height: '50%', background: '#E0F2FE', borderRadius: '50% 40% 60% 40%', transform: 'rotate(-15deg)', filter: 'blur(80px)', zIndex: 0, opacity: 0.6 }}></div>

        <div style={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '1000px', margin: '0 auto' }}>
          
          {/* Header Section */}
          <div className="template-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
            <div className="template-title-section">
              <div style={{ display: 'flex', flexDirection: 'row', gap: '10px', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Text appearance="heading-s" color="primary-grey-100">Grievance Configuration</Text>
                  <div className="custom-header-tooltip-container" style={{ display: 'flex' }}>
                    <div className="custom-header-tooltip-icon">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line>
                      </svg>
                    </div>
                    <div className="custom-header-tooltip-text" style={{ width: '280px', fontWeight: 'normal' }}>
                      Configure grievance redressal workflow, timelines, and intake methods
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bs-header-right" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <button
                onClick={handleSubmit}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: '#3B82F6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)' }}
              >
                Save Configuration
              </button>
            </div>
          </div>
          {/* Form Card */}
          <div style={{ background: '#FFFFFF', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)', border: '1px solid #E2E8F0' }}>
            <div style={{ display: 'grid', gap: '24px', maxWidth: '600px' }}>
              
              <div className="gr-dropdown-group">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Intake method (Required)</label>
                <Select
                  isMulti
                  options={optionsIntake}
                  value={optionsIntake.filter((opt) => intakeMethods.includes(opt.value))}
                  onChange={(selected) => setIntakeMethods(selected ? selected.map((s) => s.value) : [])}
                  styles={customStyles}
                  isDisabled={optionsIntake.length === 1 && intakeMethods.includes(optionsIntake[0].value)}
                />
                <Text appearance="body-xs" color="primary-grey-80">This will be user’s default language for consent communication</Text>
              </div>

              <div className="gr-input">
                <InputFieldV2
                  className="systemConfig-input"
                  label="Grievance Callback Endpoint (Required)"
                  helperText="Callback URL to notify the Data Fiduciary"
                  placeholder="https://example.com/callback"
                  name="endpointUrl"
                  value={endpointUrl}
                  onChange={handleEndpointUrlChange}
                  stateText={isEndpointUrlValid ? "" : "Please enter a valid URL (e.g. https://api.example.com)"}
                />
              </div>

              <div className="gr-dropdown-group">
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500', color: '#374151' }}>Workflow steps (Required)</label>
                <Select
                  isMulti
                  options={workflowOptions}
                  value={workflowOptions.filter((opt) => workflow.includes(opt.value))}
                  onChange={(selected) => setWorkflow(selected ? selected.map((s) => s.value) : [])}
                  styles={customStyles}
                  isDisabled={true}
                />
                <Text appearance="body-xs" color="primary-grey-80">The steps grievance redressal will follow</Text>
              </div>

              <div className="gr-input">
                <InputFieldV2
                  className="systemConfig-input"
                  label="SLA timelines in days (Required)"
                  helperText="Estimated time to handle grievances"
                  value={slaInput || "7"}
                  onChange={(e) => handleSlaChange(e)}
                  placeholder="Enter a number"
                  type='number'
                />
              </div>

              <div className="gr-input">
                <InputFieldV2
                  className="systemConfig-input"
                  label="Escalation policy in days (Required)"
                  placeholder="Enter a number"
                  name="escalationPolicy"
                  value={escalationInput || "2"}
                  onChange={(e) => handleEscalationChange(e)}
                  helperText="Time frame to escalate the grievance request"
                  type="number"
                />
              </div>

            </div>
          </div>
        </div>
      </div>
      <ToastContainer
        position="bottom-left"
        autoClose={3000}
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable
        closeButton={false}
        toastClassName={() => "toast-wrapper"}
        transition={Slide}
      />
    </>
  );
};

export default Grievance;
